import { DatePipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  model,
  OnInit,
  signal,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { UserService } from '../user.service';
import {
  ChevronDownIconComponent,
  EditIconComponent,
  SearchIconComponent,
} from '@kuiper/icons';
import { UserData } from '../user-model';
import { FormsModule } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { Group } from '../../groups/group-model';
import { BaseCardList } from '../../base/base-card-list';
import { ApiResponse } from '../../../../../common/models/api-response.model';
import { ADMIN_ROUTES } from '../../../../../common/constants/admin.routing.constants';
import { Option } from '../../../../../common/models/option.model';
import { TenantPathPipe } from '../../../../../common/pipes/tenant-path/tenant-path.pipe';
import { UserStatusPipe } from '../../../../../common/pipes/user-status/user-status.pipe';
import { TenantService } from '../../../../../common/services/tenant/tenant.service';
import { AvatarPipe } from '../../../../../../ui-components/pipes/avatar/avatar.pipe';
import { OverlayLoaderComponent } from '../../../../../common/components/overlay-loader/overlay-loader.component';
import { BadgeComponent } from '../../../../../../ui-components/lib/badge/badge.component';
import { GroupUserCardComponent } from '../../../../../common/components/group-user-card/group-user-card.component';
import { PaginatorComponent } from '../../../../../../ui-components/lib/paginator/paginator.component';
import { LoaderComponent } from '../../../../../../ui-components/lib/loader/loader.component';
import {
  injectFloatingLayout,
  injectFloatingPageClasses,
  provideSystemTabsLayout,
} from '../../../../../common/helpers/system-tabs-layout/system-tabs-layout';

@Component({
  selector: 'lib-user-details',
  imports: [
    DatePipe,
    NgTemplateOutlet,
    AvatarPipe,
    OverlayLoaderComponent,
    RouterModule,
    BadgeComponent,
    UserStatusPipe,
    GroupUserCardComponent,
    FormsModule,
    TranslocoPipe,
    PaginatorComponent,
    LoaderComponent,
    SearchIconComponent,
    ChevronDownIconComponent,
    EditIconComponent,
    TenantPathPipe,
  ],
  providers: [provideSystemTabsLayout()],
  templateUrl: './user-details.component.html',
  styleUrl: './user-details.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class UserDetailsComponent
  extends BaseCardList
  implements OnInit, AfterViewInit, OnDestroy
{
  readonly ADMIN_ROUTES = ADMIN_ROUTES;
  protected readonly floating = injectFloatingLayout();
  protected readonly floatingPageClasses = injectFloatingPageClasses();

  route = inject(ActivatedRoute);
  userService = inject(UserService);
  router = inject(Router);
  tenantService = inject(TenantService);

  isLoading = signal<boolean>(false);
  userDetails = model.required<UserData>();
  userId = signal<string>('');

  ngOnInit(): void {
    this.route.params.subscribe((params: any) => {
      this.userId.set(params.id);
      this.getUserById();
      this.getCardList();
    });
  }

  ngAfterViewInit(): void {
    this.searchTermWrapper();
  }

  getUserById() {
    this.isLoading.set(true);
    this.userService.getUserDetails(this.userId()).subscribe({
      next: (res: { data: UserData }) => {
        this.userDetails.set(res.data);
      },
      error: () => {
        this.router.navigateByUrl(
          this.tenantService.adminPath(`${ADMIN_ROUTES.USERS_DATABASE.LIST}`)
        );
      },
      complete: () => {
        this.isLoading.set(false);
      },
    });
  }

  getCardList(searchName = '') {
    this.isCardListLoading.set(true);
    this.userService
      .getUserGroups(this.userId(), this.cardListCurrentPage(), 15, searchName)
      .subscribe({
        next: (response: ApiResponse<Group>) => {
          const users = this.mapAssignedGroups(response.data);
          this.handleCardListResponse(users, response.meta);
        },
      });
  }

  mapAssignedGroups(groups: Group[]): Option[] {
    return groups
      .map((group: any) => {
        return {
          label: group.name,
          value: group.id.toString(),
          image: group.image,
        };
      })
      .sort((a, b) => {
        return a.label.localeCompare(b.label);
      });
  }

  goBack() {
    history.back();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
